import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Search, Pencil, Trash2, Users, X, Plus, Monitor } from "lucide-react";
import QuickPaymentPanel from "../components/QuickPaymentPanel";
import PosPaymentStep from "../components/PosPaymentStep";
import { useProducts } from "../context/ProductsContext";
import EditQuantityModal from "../components/EditQuantityModal";
import { AddProductModal } from "../components/AddProductModal";
import AddCustomerModal from "../components/customers/AddCustomerModal";
import { useAuth } from "../auth/AuthContext";
import { Product } from "../types/product";
import { BusinessService } from "../types/businessService";
import {
  invalidatePosCatalog,
  invalidateProductsCatalog,
  loadPosCatalog,
  PosCatalogData,
} from "../services/catalogService";
import { catalogCacheKey, getCatalogUserKey, readCatalogCache } from "../lib/catalogCache";
import GlobalLoader from "../components/ui/GlobalLoader";
import { useToast } from "../hooks/useToast";
import { useEmployees } from "../hooks/useEmployees";
import {
  calculatePosCartTotals,
  getServiceNetPrice,
  getServiceTaxPercent,
  PosCartDealItem,
  PosCartServiceItem,
  PosTax,
} from "../lib/posCart";
import { cn } from "../lib/utils";
import { usePosCustomerDisplay } from "../hooks/usePosCustomerDisplay";
import {
  isPosDualScreenEnabled,
  patchPosDisplayState,
  PosDisplayLineItem,
  PosDisplayPaymentStatus,
} from "../lib/posDisplaySync";
import { getActiveDeals, ManageDeal } from "../services/manageDealsApi";

const POS_BREAKPOINT = 1030; // px — two-column layout kicks in above this width
const WALKIN_CUSTOMER_ID = 4;
const WALKIN_CUSTOMER = {
  id: WALKIN_CUSTOMER_ID,
  name: "Walk-in Customer",
  email: "",
  mobile: "",
};

const readInitialPosCatalog = (): PosCatalogData | null => {
  const userKey = getCatalogUserKey();
  if (!userKey) return null;
  return readCatalogCache<PosCatalogData>(catalogCacheKey("pos", userKey))?.data ?? null;
};

const POS = () => {
  const location = useLocation();
  const { user } = useAuth();
  const toast = useToast();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "";
  const quickPaymentType =
    (user?.company?.quick_payment_type as "direct" | "stylist") || "stylist";

  // Track whether the viewport is wide enough for two-column layout
  const [isWide, setIsWide] = useState(() => window.innerWidth >= POS_BREAKPOINT);
  useEffect(() => {
    const onResize = () => setIsWide(window.innerWidth >= POS_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [showQuickPaymentPanel, setShowQuickPaymentPanel] = useState(false);
  const [sidebarMode, setSidebarMode] = useState<"quick" | "paynow" | null>(
    null
  );
  const [editProductId, setEditProductId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [pendingBarcode, setPendingBarcode] = useState<string>("");
  const [dismissedBarcode, setDismissedBarcode] = useState<string>("");
  const { products: cartProducts, setProducts, deleteProduct } = useProducts();
  const { staffList } = useEmployees();

  type CatalogTab = "products" | "services" | "deals";

  // Fetch all available products (not just cart items)
  const initialCatalog = readInitialPosCatalog();
  const [allProducts, setAllProducts] = useState<Product[]>(
    () => initialCatalog?.products ?? [],
  );
  const [allServices, setAllServices] = useState<BusinessService[]>(
    () => initialCatalog?.services ?? [],
  );
  const [allTaxes, setAllTaxes] = useState<PosTax[]>(
    () => initialCatalog?.taxes ?? [],
  );
  const [allDeals, setAllDeals] = useState<ManageDeal[]>([]);
  const [cartServices, setCartServices] = useState<PosCartServiceItem[]>([]);
  const [cartDeals, setCartDeals] = useState<PosCartDealItem[]>([]);
  const [catalogTab, setCatalogTab] = useState<CatalogTab>("products");
  const [isServicesLoading, setIsServicesLoading] = useState(
    () => !(initialCatalog?.services?.length),
  );
  const [isDealsLoading, setIsDealsLoading] = useState(true);
  // Customer selection state
  const [customers, setCustomers] = useState<any[]>(
    () =>
      (initialCatalog?.customers?.length
        ? initialCatalog.customers
        : [WALKIN_CUSTOMER]) as any[],
  );
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | number>("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [isCustomerLoading, setIsCustomerLoading] = useState(false);
  const [showCustomerResults, setShowCustomerResults] = useState(false);

  // Track which product images failed to load
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(() => !(initialCatalog?.products?.length));
  const posInitializedRef = useRef(false);

  const applyCatalog = useCallback((data: PosCatalogData) => {
    setAllProducts(data.products);
    setAllServices(data.services);
    setAllTaxes(data.taxes);
    if (data.customers?.length) {
      setCustomers(data.customers as any[]);
    }
  }, []);

  const refreshCatalog = useCallback(
    async (options?: { force?: boolean; silent?: boolean }) => {
      const userId = user?.id;
      if (!userId) return;

      const userKey = String(userId);
      const cached = readCatalogCache<PosCatalogData>(catalogCacheKey("pos", userKey));
      const hasCachedProducts = Boolean(cached?.data?.products?.length);

      if (!options?.silent && !hasCachedProducts) {
        setIsLoading(true);
        setIsServicesLoading(true);
      }

      try {
        const data = await loadPosCatalog(userKey, {
          force: options?.force,
          onCached: (cached) => {
            applyCatalog(cached);
            if (!options?.silent) {
              setIsLoading(false);
              setIsServicesLoading(false);
            }
          },
        });
        applyCatalog(data);
      } catch (error) {
        console.error("Failed to load POS catalog:", error);
      } finally {
        if (!options?.silent) {
          setIsLoading(false);
          setIsServicesLoading(false);
        }
      }
    },
    [applyCatalog, user?.id],
  );

  React.useEffect(() => {
    const handleOnline = () => {
      if (user?.id) {
        refreshCatalog({ silent: true });
      }
    };
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [refreshCatalog, user?.id]);

  // Initialise cart/customer once — avoid clearing cart when auth user object refreshes
  React.useEffect(() => {
    if (posInitializedRef.current) return;
    posInitializedRef.current = true;

    const rebookData = location.state?.rebookData;

    if (rebookData) {
      if (rebookData.items && Array.isArray(rebookData.items)) {
        setProducts(rebookData.items);
      }
      if (rebookData.customer) {
        setSelectedCustomerId(rebookData.customer.id);
        setCustomerSearch(rebookData.customer.name);
      }
    } else {
      setProducts([]);
      setCartServices([]);
      setCartDeals([]);
      setSelectedCustomerId(WALKIN_CUSTOMER_ID);
      setCustomerSearch(WALKIN_CUSTOMER.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load catalog: cache first, background refresh when online + stale
  React.useEffect(() => {
    if (!user?.id) return;
    refreshCatalog({ silent: Boolean(initialCatalog?.products?.length) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  React.useEffect(() => {
    let mounted = true;
    const loadDeals = async () => {
      setIsDealsLoading(true);
      try {
        const deals = await getActiveDeals();
        if (mounted) setAllDeals(deals);
      } catch {
        if (mounted) setAllDeals([]);
      } finally {
        if (mounted) setIsDealsLoading(false);
      }
    };
    void loadDeals();
    return () => {
      mounted = false;
    };
  }, []);

  // Helper function to get full image URL
  const getImageUrl = (img: any): string | null => {
    // Ensure img is a string
    if (!img) return null;
    const imgStr = typeof img === "string" ? img : String(img);
    if (!imgStr || imgStr.trim() === "") return null;

    // If already a full URL, return as is
    if (
      imgStr.startsWith("http://") ||
      imgStr.startsWith("https://") ||
      imgStr.startsWith("data:")
    ) {
      return imgStr;
    }
    // If it's a blob URL (from file preview), return as is
    if (imgStr.startsWith("blob:")) {
      return imgStr;
    }
    // If it starts with storage/ or public/, it's a storage path
    if (imgStr.startsWith("storage/") || imgStr.startsWith("public/")) {
      const baseUrl = "https://salonify.co.uk";
      const cleanPath = imgStr.startsWith("/") ? imgStr.slice(1) : imgStr;
      return `${baseUrl}/${cleanPath}`;
    }
    // If it's a relative path starting with /, prepend base URL
    if (imgStr.startsWith("/")) {
      return `https://salonify.co.uk${imgStr}`;
    }
    // Otherwise, assume it's a storage path and prepend base URL
    return `https://salonify.co.uk/storage/${imgStr}`;
  };

  const isPlaceholderProductImage = (url: string | null): boolean => {
    if (!url) return true;
    const value = url.toLowerCase();
    return (
      value.includes("img-placeholder") ||
      value.includes("placeholder.png") ||
      value.includes("/img/placeholder")
    );
  };

  // Check if search query is a 5-digit code (barcode)
  const isBarcodeSearch = useMemo(() => {
    const trimmed = searchQuery.trim();
    return /^\d{5}$/.test(trimmed);
  }, [searchQuery]);

  // Filter products based on search
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return allProducts;
    const query = searchQuery.toLowerCase().trim();

    // If it's a 5-digit code, search barcode and SKU (exact match first, then contains)
    if (isBarcodeSearch) {
      const trimmed = searchQuery.trim();
      return allProducts.filter(
        (p) =>
          p.barcode?.trim() === trimmed ||
          p.sku?.trim() === trimmed ||
          p.barcode?.toLowerCase().includes(query) ||
          p.sku?.toLowerCase().includes(query)
      );
    }

    // Regular search: name, category, brand, barcode, or SKU
    return allProducts.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        p.category?.toLowerCase().includes(query) ||
        p.brand?.toLowerCase().includes(query) ||
        p.barcode?.toLowerCase().includes(query) ||
        p.sku?.toLowerCase().includes(query)
    );
  }, [allProducts, searchQuery, isBarcodeSearch]);

  const filteredServices = useMemo(() => {
    if (!searchQuery.trim()) return allServices;
    const query = searchQuery.toLowerCase().trim();
    return allServices.filter(
      (s) =>
        s.name.toLowerCase().includes(query) ||
        s.categoryName?.toLowerCase().includes(query),
    );
  }, [allServices, searchQuery]);

  const filteredDeals = useMemo(() => {
    if (!searchQuery.trim()) return allDeals;
    const query = searchQuery.toLowerCase().trim();
    return allDeals.filter((d) => d.title.toLowerCase().includes(query));
  }, [allDeals, searchQuery]);

  const cartTotals = useMemo(
    () => calculatePosCartTotals(cartProducts, cartServices, cartDeals),
    [cartProducts, cartServices, cartDeals],
  );

  const dualScreenEnabled = isPosDualScreenEnabled(user);
  const [displayPaymentStatus, setDisplayPaymentStatus] =
    useState<PosDisplayPaymentStatus>("idle");
  const [displayPaymentMeta, setDisplayPaymentMeta] = useState<{
    tenderLabel?: string | null;
    amountDue?: number | null;
    totalTendered?: number | null;
    changeDue?: number | null;
    amountPaid?: number | null;
  }>({});

  const completeDisplayTimerRef = useRef<number | null>(null);

  const resetCustomerDisplayPayment = useCallback(() => {
    if (completeDisplayTimerRef.current) {
      window.clearTimeout(completeDisplayTimerRef.current);
      completeDisplayTimerRef.current = null;
    }
    setDisplayPaymentStatus("idle");
    setDisplayPaymentMeta({});
  }, []);

  const pushCustomerDisplayUpdate = useCallback(
    (update: {
      status: PosDisplayPaymentStatus;
      tenderLabel?: string | null;
      amountDue?: number;
      totalTendered?: number;
      changeDue?: number;
      amountPaid?: number;
    }) => {
      setDisplayPaymentMeta((prev) => {
        const next = {
          tenderLabel:
            update.tenderLabel !== undefined
              ? update.tenderLabel
              : prev.tenderLabel ?? null,
          amountDue: update.amountDue ?? prev.amountDue ?? null,
          totalTendered: update.totalTendered ?? prev.totalTendered ?? null,
          changeDue: update.changeDue ?? prev.changeDue ?? null,
          amountPaid: update.amountPaid ?? prev.amountPaid ?? null,
        };
        patchPosDisplayState({
          paymentStatus: update.status,
          tenderLabel: next.tenderLabel,
          amountDue: next.amountDue,
          totalTendered: next.totalTendered,
          changeDue: next.changeDue,
          amountPaid: next.amountPaid,
        });
        return next;
      });
      setDisplayPaymentStatus(update.status);
    },
    [],
  );

  const displayItems = useMemo<PosDisplayLineItem[]>(() => {
    const productLines: PosDisplayLineItem[] = cartProducts.map((p) => {
      const qty = p.quantity ?? 1;
      const unit =
        p.discountedPrice !== undefined &&
        p.discountedPrice !== null &&
        p.discountedPrice < p.price
          ? p.discountedPrice
          : p.price;
      return {
        id: String(p.id),
        name: p.name,
        quantity: qty,
        unitPrice: unit,
        lineTotal: unit * qty,
        kind: "product",
      };
    });
    const serviceLines: PosDisplayLineItem[] = cartServices.map((s) => ({
      id: String(s.id),
      name: s.name,
      quantity: s.quantity,
      unitPrice: s.price,
      lineTotal: s.price * s.quantity,
      kind: "service",
    }));
    return [...serviceLines, ...productLines];
  }, [cartProducts, cartServices]);

  const effectivePaymentStatus: PosDisplayPaymentStatus = useMemo(() => {
    if (displayPaymentStatus === "complete") return "complete";
    if (displayPaymentStatus === "saving") return "saving";
    if (sidebarMode) {
      return displayPaymentStatus === "idle" || displayPaymentStatus === "active"
        ? "awaiting"
        : displayPaymentStatus;
    }
    if (displayItems.length > 0) return "active";
    return "idle";
  }, [displayPaymentStatus, sidebarMode, displayItems.length]);

  const {
    popupBlocked,
    displayOpen,
    openDisplay,
  } = usePosCustomerDisplay({
    enabled: dualScreenEnabled,
    companyName: user?.company?.company_name || "",
    currencySymbol,
    customerName:
      customers.find((c) => c.id === selectedCustomerId)?.name || null,
    items: displayItems,
    originalNet: cartTotals.originalNet,
    discount: cartTotals.discount,
    tax: cartTotals.tax,
    total: cartTotals.total,
    paymentStatus: effectivePaymentStatus,
    tenderLabel: displayPaymentMeta.tenderLabel,
    amountDue: displayPaymentMeta.amountDue ?? cartTotals.total,
    totalTendered: displayPaymentMeta.totalTendered,
    changeDue: displayPaymentMeta.changeDue,
    amountPaid: displayPaymentMeta.amountPaid,
  });

  const cartItemCount =
    cartProducts.reduce((sum, p) => sum + (p.quantity ?? 1), 0) +
    cartServices.reduce((sum, s) => sum + s.quantity, 0) +
    cartDeals.reduce((sum, d) => sum + d.quantity, 0);

  // Reset dismissed barcode when search query changes significantly
  useEffect(() => {
    if (searchQuery.trim().length !== 5 || !isBarcodeSearch) {
      setDismissedBarcode("");
    }
  }, [searchQuery, isBarcodeSearch]);

  // Handle barcode search with no results - open modal
  useEffect(() => {
    const trimmedBarcode = searchQuery.trim();
    if (
      catalogTab === "products" &&
      isBarcodeSearch &&
      filteredProducts.length === 0 &&
      trimmedBarcode.length === 5 &&
      !showAddProductModal &&
      dismissedBarcode !== trimmedBarcode
    ) {
      setPendingBarcode(trimmedBarcode);
      setShowAddProductModal(true);
    }
  }, [
    isBarcodeSearch,
    filteredProducts.length,
    searchQuery,
    showAddProductModal,
    dismissedBarcode,
  ]);

  const handleAddDealToCart = (deal: ManageDeal) => {
    setCartDeals((prev) => {
      const existing = prev.find((d) => d.id === deal.id);
      if (existing) {
        return prev.map((d) =>
          d.id === deal.id ? { ...d, quantity: d.quantity + 1 } : d,
        );
      }
      return [
        ...prev,
        {
          id: deal.id,
          name: deal.title,
          price: Number(deal.deal_amount || 0),
          originalPrice: Number(deal.original_amount || deal.deal_amount || 0),
          quantity: 1,
          itemsCount: deal.items_count ?? deal.items?.length ?? 0,
        },
      ];
    });
  };

  const handleAddServiceToCart = (service: BusinessService) => {
    const netPrice = getServiceNetPrice(service);
    const taxPercent = getServiceTaxPercent(service, allTaxes);

    setCartServices((prev) => {
      const existing = prev.find((s) => s.id === service.id);
      if (existing) {
        return prev.map((s) =>
          s.id === service.id ? { ...s, quantity: s.quantity + 1 } : s,
        );
      }
      return [
        ...prev,
        {
          id: service.id,
          name: service.name,
          price: netPrice,
          quantity: 1,
          taxPercent,
          durationLabel: service.durationLabel,
        },
      ];
    });
  };

  const handleRemoveService = (serviceId: string) => {
    setCartServices((prev) => prev.filter((s) => s.id !== serviceId));
  };

  const defaultEmployeeId =
    staffList.length > 0 ? staffList[0].id : user?.id ?? null;

  // Add product to cart
  const handleAddToCart = (product: Product) => {
    setProducts((prev) => {
      const existing = prev.find((p) => p.id === product.id);
      if (existing) {
        // If product already in cart, increase quantity
        return prev.map((p) =>
          p.id === product.id ? { ...p, quantity: (p.quantity ?? 1) + 1 } : p
        );
      } else {
        // Add new product to cart
        return [...prev, { ...product, quantity: 1 }];
      }
    });
  };

  // Handle product creation from modal
  const handleProductCreated = async (product: Product) => {
    if (user?.id) {
      invalidateProductsCatalog(String(user.id));
      await refreshCatalog({ force: true, silent: true });
    }

    handleAddToCart(product);

    // Clear search query and dismissed barcode
    setSearchQuery("");
    setDismissedBarcode("");
  };

  // Removed background color generation - using simple white background for all products

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex flex-col md:flex-row items-center gap-3 mb-4 flex-shrink-0">
        <div className="flex rounded-lg border border-[#E5E0EB] p-1 bg-white shrink-0">
          <button
            type="button"
            onClick={() => setCatalogTab("products")}
            className={cn(
              "px-4 py-2 rounded-md text-sm font-bold transition-colors",
              catalogTab === "products"
                ? "bg-[#885ABB] text-white"
                : "text-[#402B69] hover:bg-purple-50",
            )}
          >
            Products
          </button>
          <button
            type="button"
            onClick={() => setCatalogTab("services")}
            className={cn(
              "px-4 py-2 rounded-md text-sm font-bold transition-colors",
              catalogTab === "services"
                ? "bg-[#885ABB] text-white"
                : "text-[#402B69] hover:bg-purple-50",
            )}
          >
            Services
          </button>
          <button
            type="button"
            onClick={() => setCatalogTab("deals")}
            className={cn(
              "px-4 py-2 rounded-md text-sm font-bold transition-colors",
              catalogTab === "deals"
                ? "bg-[#885ABB] text-white"
                : "text-[#402B69] hover:bg-purple-50",
            )}
          >
            Deals
          </button>
        </div>
        {dualScreenEnabled && (
          <button
            type="button"
            onClick={() => openDisplay(true)}
            className={cn(
              "shrink-0 inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition",
              displayOpen
                ? "border-green-200 bg-green-50 text-green-700"
                : popupBlocked
                  ? "border-amber-200 bg-amber-50 text-amber-700"
                  : "border-[#E5E0EB] bg-white text-[#402B69] hover:bg-purple-50",
            )}
            title={
              popupBlocked
                ? "Popup blocked — click to open customer display"
                : displayOpen
                  ? "Customer display is open"
                  : "Open customer display"
            }
          >
            <Monitor className="w-4 h-4" />
            {popupBlocked
              ? "Open Customer Display"
              : displayOpen
                ? "Display Open"
                : "Customer Display"}
          </button>
        )}
        <div className="relative w-full">
          <input
            type="text"
            placeholder={
              catalogTab === "products"
                ? "Search products..."
                : catalogTab === "services"
                  ? "Search services..."
                  : "Search deals..."
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full border border-[#E5E0EB] rounded-md py-3 pl-4 pr-10 text-[16px] font-medium text-[#402B69] placeholder:text-[#402B69]/60 focus:outline-none shadow-sm"
          />
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#885ABB]" />
        </div>
      </div>

      {/* Main Grid / Stack */}
      <div className={`${isWide ? "grid grid-cols-3 overflow-hidden" : "flex flex-col overflow-y-auto"} gap-6 items-stretch flex-1 mb-4 custom-scrollbar`}>
        {/* Left Section - Products (Order 2 on mobile, 1 on desktop) */}
        <div className={`${isWide ? "col-span-2 order-1 overflow-y-auto flex-shrink" : "order-2 flex-shrink-0"} bg-white rounded-xl p-4 sm:p-6 relative shadow-sm border border-gray-100`}>
          <div className="h-full min-h-[280px]">
            {/* Products panel — kept mounted so tab switch doesn't flash loader */}
            <div className={cn(catalogTab !== "products" && "hidden")}>
              {isLoading && allProducts.length === 0 ? (
                <div className="flex items-center justify-center h-full min-h-[280px]">
                  <GlobalLoader
                    fullScreen={false}
                    compensateSidebar={false}
                    description="Loading products..."
                  />
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-lg">No products found</p>
                  {searchQuery && (
                    <p className="text-sm mt-2">Try a different search term</p>
                  )}
                </div>
              ) : (
                <div className={`grid gap-4 ${isWide ? "grid-cols-4" : "grid-cols-2 md:grid-cols-3"}`}>
                  {filteredProducts.map((product) => {
                  const imageUrl = getImageUrl(
                    product.image || product.images?.[0]
                  );
                  const hasImage =
                    !!imageUrl &&
                    !isPlaceholderProductImage(imageUrl) &&
                    !brokenImages.has(product.id);

                  // Calculate Tax Percentage
                  const totalTaxPercent = product.taxes?.reduce((sum, tax) => sum + tax.percent, 0) || 0;

                  // Calculate Original Price (Inc Tax)
                  const originalPriceIncTax = product.price * (1 + totalTaxPercent / 100);

                  // Calculate Discounted Price (Inc Tax)
                  let discountedPriceIncTax: number | null = null;
                  if (product.discountedPrice !== undefined &&
                    product.discountedPrice !== null &&
                    product.discountedPrice < product.price) {
                    discountedPriceIncTax = product.discountedPrice * (1 + totalTaxPercent / 100);
                  }

                  const displayPrice = discountedPriceIncTax !== null ? discountedPriceIncTax : originalPriceIncTax;
                  const activeNetPrice = (product.discountedPrice && product.discountedPrice < product.price) ? product.discountedPrice : product.price;
                  const activeTaxAmount = activeNetPrice * (totalTaxPercent / 100);

                  return (
                    <div
                      key={product.id}
                      onClick={() => handleAddToCart(product)}
                      className={cn(
                        "aspect-square rounded-md cursor-pointer overflow-hidden hover:opacity-90 transition-opacity relative border-2",
                        hasImage ? "border-[#E5E0EB] bg-white" : "border-[#885ABB] bg-[#885ABB]"
                      )}
                    >
                      {hasImage && (
                        <img
                          src={imageUrl || ""}
                          alt=""
                          className="absolute inset-0 h-full w-full object-cover"
                          onError={() => {
                            setBrokenImages((prev) =>
                              new Set(prev).add(product.id)
                            );
                          }}
                        />
                      )}

                      <div
                        className={cn(
                          "relative z-[1] h-full flex flex-col justify-between p-3 text-left",
                          hasImage && "bg-black/35"
                        )}
                      >
                        <h3
                          className={cn(
                            "text-sm lg:text-[18px] font-bold leading-snug break-words line-clamp-3",
                            hasImage ? "text-white drop-shadow-sm" : "text-white"
                          )}
                        >
                          {product.name}
                        </h3>
                        <div className="flex flex-col items-start">
                          {discountedPriceIncTax !== null && (
                            <span
                              className={cn(
                                "text-xs line-through leading-tight",
                                hasImage ? "text-white/70" : "text-white/70"
                              )}
                            >
                              {currencySymbol}{originalPriceIncTax.toFixed(2)}
                            </span>
                          )}
                          <p className="text-base lg:text-[18px] font-extrabold text-white leading-tight">
                            {currencySymbol}{displayPrice.toFixed(2)}
                          </p>
                          {(product.sku || product.barcode) && (
                            <div className="text-[10px] lg:text-[11px] text-white/90 font-semibold leading-tight mt-1 break-all line-clamp-2">
                              {product.sku && <span>SKU: {product.sku}</span>}
                              {product.sku && product.barcode && <span className="mx-1">·</span>}
                              {product.barcode && <span>Barcode: {product.barcode}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                </div>
              )}
            </div>

            {/* Services panel — same grid + card style as products */}
            <div className={cn(catalogTab !== "services" && "hidden")}>
              {isServicesLoading && allServices.length === 0 ? (
                <div className="flex items-center justify-center h-full min-h-[280px]">
                  <GlobalLoader
                    fullScreen={false}
                    compensateSidebar={false}
                    description="Loading services..."
                  />
                </div>
              ) : filteredServices.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-lg">No services found</p>
                  {searchQuery && (
                    <p className="text-sm mt-2">Try a different search term</p>
                  )}
                </div>
              ) : (
                <div className={`grid gap-4 ${isWide ? "grid-cols-4" : "grid-cols-2 md:grid-cols-3"}`}>
                  {filteredServices.map((service) => {
                    const netPrice = getServiceNetPrice(service);
                    const taxPercent = getServiceTaxPercent(service, allTaxes);
                    const grossPrice = netPrice * (1 + taxPercent / 100);

                    return (
                      <div
                        key={service.id}
                        onClick={() => handleAddServiceToCart(service)}
                        className="bg-white border-2 border-[#E5E0EB] aspect-square rounded-md p-4 cursor-pointer overflow-hidden hover:opacity-90 transition-opacity relative flex flex-col justify-end"
                      >
                        <div className="absolute inset-0 bg-gradient-to-t from-[#402B69]/10 via-transparent to-transparent pointer-events-none" />
                        <div className="relative z-[1]">
                          <h3 className="text-sm lg:text-[18px] font-bold text-[#402B69] line-clamp-2">
                            {service.name}
                          </h3>
                          <p className="text-[11px] lg:text-xs text-[#885ABB] font-semibold mt-1 line-clamp-1">
                            {service.durationLabel}
                            {service.categoryName ? ` · ${service.categoryName}` : ""}
                          </p>
                          <p className="text-base lg:text-[18px] font-extrabold text-[#402B69] mt-2">
                            {currencySymbol}{grossPrice.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Deals panel */}
            <div className={cn(catalogTab !== "deals" && "hidden")}>
              {isDealsLoading && allDeals.length === 0 ? (
                <div className="flex items-center justify-center h-full min-h-[280px]">
                  <GlobalLoader
                    fullScreen={false}
                    compensateSidebar={false}
                    description="Loading deals..."
                  />
                </div>
              ) : filteredDeals.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-lg">No deals found</p>
                  <p className="text-sm mt-2">
                    Create deals under Manage → Deals
                  </p>
                </div>
              ) : (
                <div className={`grid gap-4 ${isWide ? "grid-cols-4" : "grid-cols-2 md:grid-cols-3"}`}>
                  {filteredDeals.map((deal) => {
                    const dealPrice = Number(deal.deal_amount || 0);
                    const original = Number(deal.original_amount || 0);
                    const hasSaving = original > dealPrice;

                    return (
                      <div
                        key={deal.id}
                        onClick={() => handleAddDealToCart(deal)}
                        className="bg-white border-2 border-[#E5E0EB] aspect-square rounded-md cursor-pointer overflow-hidden hover:opacity-90 transition-opacity relative"
                      >
                        <div className="absolute inset-0 bg-gradient-to-t from-[#885ABB]/15 via-transparent to-transparent pointer-events-none" />
                        <div className="relative z-[1] h-full flex flex-col justify-between p-3 text-left">
                          <div>
                            <h3 className="text-sm lg:text-[18px] font-bold text-[#402B69] leading-snug break-words line-clamp-3">
                              {deal.title}
                            </h3>
                            <p className="text-[11px] lg:text-xs text-[#885ABB] font-semibold mt-1">
                              {(deal.items_count ?? deal.items?.length ?? 0)} items
                            </p>
                          </div>
                          <div className="flex flex-col items-start">
                            {hasSaving && (
                              <p className="text-xs text-gray-400 line-through leading-tight">
                                {currencySymbol}{original.toFixed(2)}
                              </p>
                            )}
                            <p className="text-base lg:text-[18px] font-extrabold text-[#402B69] leading-tight">
                              {currencySymbol}{dealPrice.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Right Section - Cart (Order 1 on mobile, 2 on desktop) */}
        <div className={`${isWide ? "col-span-1 order-2 h-full flex-shrink" : "order-1 flex-shrink-0"} bg-white rounded-xl p-4 sm:p-6 flex flex-col overflow-hidden shadow-sm border border-gray-100`}>
          {/* Customer Selection */}
          <div className="mb-6 relative">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[15px] font-bold text-[#402B69] flex items-center gap-2">
                <Users className="w-5 h-5" />
                Select Customer
              </label>
              {!selectedCustomerId && (
                <button
                  type="button"
                  onClick={() => setShowAddCustomerModal(true)}
                  className="w-9 h-9 rounded-full bg-[#885ABB] text-white flex items-center justify-center hover:bg-[#6f3fa8] transition-colors shadow-sm"
                  title="Add new customer"
                  aria-label="Add new customer"
                >
                  <Plus className="w-5 h-5" strokeWidth={2.5} />
                </button>
              )}
            </div>
            {!selectedCustomerId ? (
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search customer..."
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    setShowCustomerResults(true);
                  }}
                  onFocus={() => setShowCustomerResults(true)}
                  className="w-full border border-[#E5E0EB] rounded-md py-2.5 pl-9 pr-4 text-[16px] font-medium text-[#402B69] placeholder:text-[#402B69]/60 focus:outline-none"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#885ABB]" />

                {showCustomerResults && customerSearch.trim().length > 0 && (
                  <div className="absolute top-full left-0 right-0 z-[100] mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                    {customers
                      .filter(c =>
                        c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
                        c.email?.toLowerCase().includes(customerSearch.toLowerCase()) ||
                        c.mobile?.includes(customerSearch)
                      )
                      .map(c => (
                        <div
                          key={c.id}
                          onClick={() => {
                            setSelectedCustomerId(c.id);
                            setCustomerSearch(c.name);
                            setShowCustomerResults(false);
                          }}
                          className="px-4 py-2 hover:bg-purple-50 cursor-pointer flex items-center gap-3 border-b border-gray-50 last:border-0"
                        >
                          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-xs font-bold text-[#885ABB]">
                            {c.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-base font-bold text-[#402B69]">{c.name}</p>
                            <p className="text-sm text-gray-500">{c.mobile || c.email}</p>
                          </div>
                        </div>
                      ))}
                    {customers.filter(c =>
                      c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
                      c.email?.toLowerCase().includes(customerSearch.toLowerCase()) ||
                      c.mobile?.includes(customerSearch)
                    ).length === 0 && (
                        <div className="px-4 py-3 text-center text-gray-500 text-sm">
                          No customers found
                        </div>
                      )}
                  </div>
                )}
              </div>
            ) : (
              (() => {
                const selectedCustomer = customers.find(c => c.id === selectedCustomerId);
                const isWalkIn = selectedCustomerId === WALKIN_CUSTOMER_ID;
                // If we have an ID but can't find the customer yet, and we are loading,
                // show a loader for real customers, but NOT for Walk-in (we can render it instantly).
                if (!selectedCustomer && isCustomerLoading && !isWalkIn) {
                  return (
                    <div className="flex items-center justify-center p-4 bg-purple-50 border border-purple-100 rounded-lg">
                      <div className="flex items-center gap-2 text-[#885ABB] text-sm font-bold">
                        <div className="w-4 h-4 border-2 border-[#885ABB] border-t-transparent rounded-full animate-spin" />
                        <span>Loading Customer...</span>
                      </div>
                    </div>
                  );
                }

                // For Walk-in Customer, if it's selected but not yet in the loaded list,
                // fall back to a local object so UI feels instant.
                const effectiveCustomer =
                  selectedCustomer ||
                  (isWalkIn
                    ? {
                        id: WALKIN_CUSTOMER_ID,
                        name: "Walk-in Customer",
                        email: "",
                        mobile: "",
                      }
                    : undefined);

                // If still not found (and not loading, or found), handle normally.
                // Note: If strictly not found but ID exists (e.g. deleted customer), we might want safe fallsbacks,
                // but usually the list eventually loads. For now we use optional chaining as before.
                return (
                  <div className="flex items-center justify-between bg-purple-50 border border-purple-100 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-200 flex items-center justify-center text-sm font-bold text-[#885ABB]">
                        {effectiveCustomer?.name?.charAt(0).toUpperCase() || "?"}
                      </div>
                      <div>
                        <p className="text-base font-bold text-[#402B69]">
                          {effectiveCustomer?.name || "Unknown Customer"}
                        </p>
                        <p className="text-sm text-[#885ABB] font-medium">
                          {effectiveCustomer?.mobile || effectiveCustomer?.email || ""}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedCustomerId("");
                        setCustomerSearch("");
                      }}
                      className="p-1.5 hover:bg-white rounded-full text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })()
            )}
          </div>

          {/* Cart items — services + products */}
          <div className="flex-1 overflow-y-auto space-y-4 mb-6 pr-2 custom-scrollbar">
            {cartItemCount === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <p className="text-base">Cart is empty</p>
                <p className="text-sm mt-1">Add products, services or deals to continue</p>
              </div>
            ) : (
              <>
                {cartDeals.map((deal) => (
                  <div
                    key={`deal-${deal.id}`}
                    className="flex items-center justify-between gap-3 py-3 border-b border-gray-50"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-[#402B69] truncate">
                        {deal.name}
                      </p>
                      <p className="text-[11px] text-[#885ABB] font-semibold">
                        Deal · {deal.itemsCount} items · Qty {deal.quantity}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm font-bold text-[#402B69]">
                        {currencySymbol}
                        {(deal.price * deal.quantity).toFixed(2)}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setCartDeals((prev) =>
                            prev.filter((d) => d.id !== deal.id),
                          )
                        }
                        className="p-1.5 rounded-md text-red-500 hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
                {cartServices.map((service) => {
                  const qty = service.quantity;
                  const lineNet = service.price * qty;
                  const lineTax = lineNet * (service.taxPercent / 100);
                  const lineGross = lineNet + lineTax;

                  return (
                    <div
                      key={`service-${service.id}`}
                      className="group border-b border-[#E5E0EB] pb-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="text-[11px] font-bold uppercase tracking-wide text-[#885ABB] shrink-0">
                            Svc
                          </span>
                          <span className="text-[17px] font-bold text-[#402B69] line-clamp-2 break-words">
                            {service.name}
                          </span>
                          <span className="text-[15px] text-[#885ABB] font-extrabold px-1.5 shrink-0 bg-purple-50 rounded">
                            x{qty}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-[17px] font-bold text-[#402B69]">
                            {currencySymbol}{lineGross.toFixed(2)}
                          </span>
                          <button
                            onClick={() => handleRemoveService(service.id)}
                            className="p-1 hover:bg-red-50 rounded-lg text-red-500"
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {cartProducts.map((product) => {
                // Calculate Tax Percentage
                const totalTaxPercent = product.taxes?.reduce((sum, tax) => sum + tax.percent, 0) || 0;

                // Calculate Net and Tax portions
                const baseNetPrice = (product.discountedPrice && product.discountedPrice < product.price) ? product.discountedPrice : product.price;
                const taxAmountPerUnit = baseNetPrice * (totalTaxPercent / 100);
                const finalPricePerUnit = baseNetPrice + taxAmountPerUnit;

                const qty = product.quantity ?? 1;
                const totalNet = baseNetPrice * qty;
                const totalTax = taxAmountPerUnit * qty;
                const totalGross = finalPricePerUnit * qty;

                return (
                  <div
                    key={product.id}
                    className="group border-b border-[#E5E0EB] pb-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      {/* Left side: name + qty */}
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-[17px] font-bold text-[#402B69] line-clamp-2 break-words">
                          {product.name}
                        </span>
                        <span className="text-[15px] text-[#885ABB] font-extrabold px-1.5 shrink-0 bg-purple-50 rounded">
                          x{qty}
                        </span>
                      </div>

                      {/* Right side: Total Price with smaller breakdown info */}
                      <div className="flex items-center gap-3">
                        <div className="text-right flex flex-col items-end">
                          {product.discountedPrice !== undefined &&
                            product.discountedPrice !== null &&
                            product.discountedPrice < product.price && (
                              <span className="text-[10px] text-gray-400 line-through">
                                {currencySymbol}
                                {(product.price * (1 + totalTaxPercent / 100) * qty).toFixed(2)}
                              </span>
                            )}
                          <span className="text-[17px] font-bold text-[#402B69]">
                            {currencySymbol}{totalGross.toFixed(2)}
                          </span>
                          <span className="hidden xl:inline text-[9px] text-gray-400 ml-1">
                            ({currencySymbol}{totalNet.toFixed(2)} + {currencySymbol}{totalTax.toFixed(2)} Т)
                          </span>
                        </div>

                        {/* Hover Actions */}
                        <div className="flex items-center gap-2 transition-opacity">
                          <button
                            onClick={() => setEditProductId(product.id)}
                            className="p-1 hover:bg-[#885ABB]/10 rounded-lg text-[#885ABB]"
                            title="Edit Quantity"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteProduct(product.id)}
                            className="p-1 hover:bg-red-50 rounded-lg text-red-500"
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              </>
            )}
          </div>

          {/* Total + Buttons (footer) */}
          <div className="flex-shrink-0 pt-2 bg-white">
            <div className="flex flex-col gap-2 border-t border-b pb-3 border-[#E5E0EB] pt-4 mb-6">
              <div className="flex justify-between items-center text-sm pos13:text-[20px] text-gray-500 font-medium">
                <span>Subtotal</span>
                <span>{currencySymbol}{cartTotals.originalNet.toFixed(2)}</span>
              </div>
              {cartTotals.discount > 0 && (
                <div className="flex justify-between items-center text-sm text-red-500 font-medium">
                  <span>Discount</span>
                  <span>-{currencySymbol}{cartTotals.discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center text-sm pos13:text-[20px] text-gray-400 font-medium">
                <span>Tax</span>
                <span>{currencySymbol}{cartTotals.tax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center mt-2 pt-2 border-t border-dashed border-gray-100">
                <span className="text-[32px] font-normal text-[#402B69]">Due</span>
                <span className="text-[32px] font-normal text-[#402B69]">
                  {currencySymbol}{cartTotals.total.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  if (!selectedCustomerId) {
                    toast.error("Please select a customer first");
                    return;
                  }
                  if (cartItemCount === 0) {
                    toast.error("Your cart is empty");
                    return;
                  }
                  setSidebarMode("paynow");
                }}
                className="flex-1 bg-[#885ABB] text-white rounded-md py-3.5 text-base font-extrabold hover:bg-[#6f3fa8] transition"
              >
                PAY NOW
              </button>
            </div>
          </div>
        </div>
      </div>
      {
        editProductId && (
          <EditQuantityModal
            productId={editProductId}
            onClose={() => setEditProductId(null)}
          />
        )
      }

      {
        showQuickPaymentPanel && (
          <QuickPaymentPanel
            onClose={() => setShowQuickPaymentPanel(false)}
            onProceed={() => {
              setShowQuickPaymentPanel(false);
              setSidebarMode("quick");
            }}
            onPaymentComplete={(bookingId: number | string) => {
              console.log("Quick Payment completed, booking ID:", bookingId);
              setProducts([]);
              setCartServices([]);
              setCartDeals([]);
              setSelectedCustomerId(WALKIN_CUSTOMER_ID);
              setCustomerSearch(WALKIN_CUSTOMER.name);
              setDisplayPaymentStatus("idle");
              setDisplayPaymentMeta({});
            }}
            quickPaymentType={quickPaymentType}
          />
        )
      }

      {
        sidebarMode && (
          <PosPaymentStep
            showStoreUsageButton
            selectedService={{
              id: "service-1",
              name: "Custom Amount",
              duration: "60 mins",
              price: `${currencySymbol}35`,
            }}
            selectedCustomer={customers.find(c => c.id === selectedCustomerId)?.name || "Guest"}
            appointmentTime={new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}
            tip={0}
            setTip={() => { }}
            goToFinalPayment={() => setSidebarMode(null)}
            onClose={() => {
              setSidebarMode(null);
              resetCustomerDisplayPayment();
            }}
            mode={sidebarMode}
            locationId={user?.company?.location_id || user?.location_id || 1}
            userId={selectedCustomerId}
            employees={
              cartServices.length > 0
                ? cartServices.map(() => defaultEmployeeId)
                : defaultEmployeeId
                  ? [defaultEmployeeId]
                  : undefined
            }
            services={cartServices.map((s) => ({
              id: s.id,
              quantity: s.quantity,
              price: s.price,
              taxPercent: s.taxPercent,
            }))}
            deals={cartDeals.map((d) => ({
              id: d.id,
              quantity: d.quantity,
              price: d.price,
            }))}
            onPaymentComplete={(bookingId: number | string) => {
              console.log("Payment completed, booking ID:", bookingId);
              setSidebarMode(null);
              setProducts([]);
              setCartServices([]);
              setCartDeals([]);
              setSelectedCustomerId(WALKIN_CUSTOMER_ID);
              setCustomerSearch(WALKIN_CUSTOMER.name);
              resetCustomerDisplayPayment();
            }}
            onCustomerDisplayUpdate={(update) => {
              if (update.status === "complete") {
                pushCustomerDisplayUpdate({
                  ...update,
                  status: "saving",
                });
                if (completeDisplayTimerRef.current) {
                  window.clearTimeout(completeDisplayTimerRef.current);
                }
                completeDisplayTimerRef.current = window.setTimeout(() => {
                  pushCustomerDisplayUpdate(update);
                  completeDisplayTimerRef.current = null;
                }, 2200);
                return;
              }
              if (completeDisplayTimerRef.current && update.status !== "saving") {
                window.clearTimeout(completeDisplayTimerRef.current);
                completeDisplayTimerRef.current = null;
              }
              pushCustomerDisplayUpdate(update);
            }}
          />
        )
      }

      {
        showAddProductModal && (
          <AddProductModal
            isOpen={showAddProductModal}
            onClose={() => {
              setShowAddProductModal(false);
              setDismissedBarcode(pendingBarcode);
              setPendingBarcode("");
              setSearchQuery("");
            }}
            onProductCreated={handleProductCreated}
            initialBarcode={pendingBarcode}
          />
        )
      }
      {
        showAddCustomerModal && (
          <AddCustomerModal
            isOpen={showAddCustomerModal}
            onClose={() => setShowAddCustomerModal(false)}
            onSave={async (newCustomer) => {
              setIsCustomerLoading(true);
              try {
                if (user?.id) {
                  invalidatePosCatalog(String(user.id));
                  await refreshCatalog({ force: true, silent: true });
                }
                setSelectedCustomerId(newCustomer.id);
                setCustomerSearch(newCustomer.name);
                setShowAddCustomerModal(false);
              } finally {
                setIsCustomerLoading(false);
              }
            }}
          />
        )
      }
    </div >
  );
};

export default POS;
